/**
 * TrayDesigner — combined Catalog picker (A) + 2D plan editor (C) +
 * Fittings & BOM (E) + live NEC validator (F). 3D drag handles (B) are
 * surfaced separately in scene.js via the same data model.
 *
 * Data flows:
 *   project.cable_trays = [{id, kind:'run'|'drop'|'fitting', points:[[x,y],...],
 *                            product_sku, width_in, depth_in, material,
 *                            load_class, hangers, fitting_type, radius_in,
 *                            rotation_deg}]
 *   project.tray_design_meta = {default_product_sku, default_load_class,
 *                                largest_cable_od_in, cable_weight_lb_per_ft}
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Trash, ArrowsClockwise, Robot, FloppyDisk, Lightning,
  CheckCircle, WarningCircle, XCircle, Cube, CopySimple,
} from "@phosphor-icons/react";
import JoinTraysPanel from "./JoinTraysPanel";
import QuickJoinDialog from "./QuickJoinDialog";
import ContextMenu from "../ContextMenu";

const FITTING_LABELS = {
  horizontal_elbow_90: "90° elbow",
  horizontal_elbow_45: "45° elbow",
  horizontal_tee: "Tee",
  horizontal_cross: "Cross",
  vertical_elbow_90: "Vertical 90°",
  vertical_elbow_45: "Vertical 45°",
  reducer: "Reducer",
  dropout: "Dropout",
};


export default function TrayDesigner({ project, projectId, update }) {
  const [catalog, setCatalog] = useState({ manufacturers: [], series: [], fitting_types: [] });
  const [filter, setFilter]   = useState({ manufacturer_id: "abb_ip", series_id: "", tray_type: "Ladder",
                                            width_in: 24, depth_in: 4, material: "Aluminum" });
  const [products, setProducts] = useState([]);
  const [defaultSku, setDefaultSku] = useState(project.tray_design_meta?.default_product_sku || "");
  const [defaultLC,  setDefaultLC]  = useState(project.tray_design_meta?.default_load_class  || "NEMA 16A");
  const [largestOD,  setLargestOD]  = useState(project.largest_cable_od_in || 1.5);
  const [cableW,     setCableW]     = useState(project.cable_weight_lb_per_ft || 0);
  const [distMult,   setDistMult]   = useState(project.distributor_multiplier ?? 1.0);

  const [segments, setSegments] = useState(project.cable_trays || []);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);

  const [validation, setValidation] = useState(null);
  const [bom, setBom] = useState(null);

  const [templates, setTemplates] = useState([]);

  const [fittings, setFittings] = useState([]);
  const [insertingFitting, setInsertingFitting] = useState(null);

  // Right-click context menu state for the segments table.
  const [ctxMenu, setCtxMenu] = useState(null);
  // Quick-join dialog state — opened from the right-click menu.
  const [quickJoinSrc, setQuickJoinSrc] = useState(null);

  // Duplicate a tray segment via the backend, then merge the clone into local
  // state so the table updates instantly.  We compute the next array OUTSIDE
  // the setSegments updater so the parent's update() never runs during the
  // updater (which React treats as a state-mutation during render).
  const duplicateSegment = useCallback(async (seg_id) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/${seg_id}/duplicate`);
      const next = [...segments, data];
      setSegments(next);
      update && update({ cable_trays: next });
      toast.success(`Duplicated ${data.id_display || data.id.slice(0, 6)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Duplicate failed");
    }
  }, [projectId, segments, update]);

  // Toggle the tray's 90° rotation about its own centerline.
  const rotateSegment = useCallback(async (seg_id) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/${seg_id}/rotate`);
      const next = segments.map((s) => s.id === seg_id
        ? { ...s, rotation_axis_deg: data.rotation_axis_deg } : s);
      setSegments(next);
      update && update({ cable_trays: next });
      toast.success(`Rotated ${data.id_display || seg_id.slice(0, 6)} → ${data.rotation_axis_deg}°`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    }
  }, [projectId, segments, update]);

  // ── Load catalog + template list
  useEffect(() => {
    (async () => {
      try {
        const [cat, tpl] = await Promise.all([
          api.get("/tray-catalogs"),
          api.get("/tray-templates"),
        ]);
        setCatalog(cat.data);
        setTemplates(tpl.data.templates || []);
      } catch (e) { console.error("Failed to load tray catalog/templates:", e); }
    })();
  }, []);

  // Compute valid tray types / widths / depths / materials for the chosen
  // manufacturer + tray type. The catalog seed (e.g. Eaton has no Solid Bottom,
  // Legrand only Wire Mesh) made the original static option lists return zero
  // products for many combinations — that's why "Choosing parameters performs
  // no action": the products list went empty and Default product blanked.
  const seriesForMfr = useMemo(
    () => (catalog.series || []).filter((s) => s.manufacturer_id === filter.manufacturer_id),
    [catalog, filter.manufacturer_id],
  );
  const validTypes = useMemo(
    () => Array.from(new Set(seriesForMfr.map((s) => s.tray_type))),
    [seriesForMfr],
  );
  const matchingSeries = useMemo(
    () => seriesForMfr.filter((s) => s.tray_type === filter.tray_type),
    [seriesForMfr, filter.tray_type],
  );
  const validWidths = useMemo(() => {
    const all = new Set();
    matchingSeries.forEach((s) => (s.widths_in || []).forEach((w) => all.add(w)));
    return Array.from(all).sort((a, b) => a - b);
  }, [matchingSeries]);
  const validDepths = useMemo(() => {
    const all = new Set();
    matchingSeries.forEach((s) => (s.depths_in || []).forEach((d) => all.add(d)));
    return Array.from(all).sort((a, b) => a - b);
  }, [matchingSeries]);
  const validMaterials = useMemo(() => {
    const all = new Set();
    matchingSeries.forEach((s) => (s.materials || []).forEach((m) => all.add(m)));
    return Array.from(all);
  }, [matchingSeries]);

  // When the manufacturer changes, snap tray_type / width / depth / material to
  // the first valid option for that manufacturer. Same when tray_type changes.
  useEffect(() => {
    if (validTypes.length === 0) return;
    setFilter((f) => {
      const nf = { ...f };
      if (!validTypes.includes(nf.tray_type)) nf.tray_type = validTypes[0];
      return nf;
    });
  }, [validTypes]);  // eslint-disable-line

  useEffect(() => {
    if (matchingSeries.length === 0) return;
    setFilter((f) => {
      const nf = { ...f };
      if (validWidths.length > 0 && !validWidths.includes(nf.width_in)) nf.width_in = validWidths[0];
      if (validDepths.length > 0 && !validDepths.includes(nf.depth_in)) nf.depth_in = validDepths[0];
      if (validMaterials.length > 0 && !validMaterials.includes(nf.material)) nf.material = validMaterials[0];
      return nf;
    });
  }, [matchingSeries, validWidths, validDepths, validMaterials]);  // eslint-disable-line

  // ── Filter products
  useEffect(() => {
    (async () => {
      try {
        const params = {
          tray_type: filter.tray_type, material: filter.material,
          width_in: filter.width_in, depth_in: filter.depth_in,
        };
        if (filter.series_id) params.series_id = filter.series_id;
        const { data } = await api.get(`/tray-catalogs/${filter.manufacturer_id}/products`, { params });
        setProducts(data.products || []);
      } catch (e) { console.warn("Failed to filter products:", e); }
    })();
  }, [filter]);

  // ── Auto-pick the first matching product as the Default whenever the
  //    catalog filter changes AND the current defaultSku is no longer in
  //    the list. This is what makes "Choosing cable tray parameters"
  //    actually do something — the dropdown stays in sync with the filter
  //    and downstream "Apply to all" picks up the new SKU.
  useEffect(() => {
    if (!products || products.length === 0) return;
    const skus = new Set(products.map((p) => p.sku));
    if (!defaultSku || !skus.has(defaultSku)) {
      const next = products[0].sku;
      setDefaultSku(next);
      // Friendly nudge so the user sees the catalog selection landed somewhere.
      // (Skip on first mount when defaultSku was empty AND no segments exist.)
      if (segments.length > 0) {
        toast.info(`Default tray → ${next}. Hit "Apply to all" to push to ${segments.length} segment${segments.length === 1 ? "" : "s"}.`);
      }
    }
  }, [products]);  // eslint-disable-line

  // ── Sync local state from parent project (e.g. undo, auto-route)
  useEffect(() => { setSegments(project.cable_trays || []); }, [project.cable_trays]);

  // ── Run validator + BOM whenever segments change (debounced)
  const validateTimer = useRef();
  useEffect(() => {
    clearTimeout(validateTimer.current);
    validateTimer.current = setTimeout(async () => {
      try {
        const params = {};
        if (distMult && distMult > 0 && distMult <= 1) params.distributor_multiplier = distMult;
        const [v, b] = await Promise.all([
          api.get(`/projects/${projectId}/trays/validate`),
          api.get(`/projects/${projectId}/trays/bom`, { params }),
        ]);
        setValidation(v.data);
        setBom(b.data);
      } catch (e) { console.warn("Tray validate/bom failed:", e); }
    }, 350);
    return () => clearTimeout(validateTimer.current);
  }, [segments, projectId, distMult]);

  // ── Save handler
  const saveDesign = useCallback(async (next = segments) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/trays`, {
        segments: next, largest_cable_od_in: largestOD,
        cable_weight_lb_per_ft: cableW,
        default_product_sku: defaultSku, default_load_class: defaultLC,
      });
      setSegments(data.segments);
      // Mirror multiplier into the project via parent update() (PATCH on save).
      update && update({ cable_trays: data.segments,
                          largest_cable_od_in: largestOD,
                          cable_weight_lb_per_ft: cableW,
                          distributor_multiplier: distMult,
                          tray_design_meta: { default_product_sku: defaultSku,
                                              default_load_class: defaultLC,
                                              largest_cable_od_in: largestOD,
                                              cable_weight_lb_per_ft: cableW } });
    } catch (e) { toast.error("Save failed"); console.error(e); }
    finally { setBusy(false); }
  }, [projectId, segments, largestOD, cableW, defaultSku, defaultLC, distMult, update]);

  const runAutoRoute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/auto-route`);
      const segs = data.segments || [];
      setSegments(segs);
      update && update({ cable_trays: segs });
      if (segs.length === 0) {
        const reason = (data.violations || [])[0]
          || "No segments generated — make sure equipment is placed on the Size tab first.";
        toast.error(reason);
      } else {
        toast.success(`Auto-routed ${segs.length} segment(s)`);
        // Apply default SKU/load_class immediately so the rows have a catalog ref.
        if (defaultSku) await applyDefaults(defaultSku, defaultLC, false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-route failed");
    } finally { setBusy(false); }
  };

  const applyTemplate = async (templateId, overwrite = true) => {
    if (!templateId) return;
    if (!defaultSku) {
      toast.warning("Pick a default product first — segments will be created without SKUs until you apply a catalog SKU.");
    }
    if (overwrite && segments.length > 0
        && !window.confirm("Replace the existing tray design with this template?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/from-template`,
        { template_id: templateId, overwrite, apply_default_sku: true });
      const segs = data.segments || [];
      setSegments(segs);
      update && update({ cable_trays: segs });
      toast.success(`Template applied · added ${data.added} segment(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template apply failed");
    } finally { setBusy(false); }
  };

  const applyDefaults = async (sku = defaultSku, lc = defaultLC, overwrite = true) => {
    if (!sku && !lc) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/apply-defaults`,
        { product_sku: sku, load_class: lc, overwrite });
      setSegments(data.segments || []);
      update && update({ cable_trays: data.segments || [] });
      toast.success(`Applied to ${data.touched} segment(s)`);
    } catch (e) { toast.error("Apply defaults failed"); console.error(e); }
  };

  const addRun = () => {
    const L = project.length_in || 240;
    const W = project.width_in  || 180;
    const seg = {
      id: cryptoId(), kind: "run",
      points: [[24, W - 12], [L - 24, W - 12]],
      product_sku: defaultSku || null,
      load_class:  defaultLC,
    };
    const next = [...segments, seg];
    setSegments(next);
    saveDesign(next);
  };

  const removeSegment = (id) => {
    const next = segments.filter((s) => s.id !== id);
    setSegments(next);
    saveDesign(next);
  };

  const updateSegment = (id, patch) => {
    const next = segments.map((s) => s.id === id ? { ...s, ...patch } : s);
    setSegments(next);
    // Debounce save via the validator effect — but persist immediately on field changes
    saveDesign(next);
  };

  const loadFittings = async (seriesId, width) => {
    try {
      const params = {};
      if (seriesId) params.series_id = seriesId;
      if (width) params.width_in = width;
      const { data } = await api.get(`/tray-catalogs/${filter.manufacturer_id}/fittings`, { params });
      setFittings(data.fittings || []);
    } catch (e) { console.error(e); }
  };

  const insertFitting = async (point, sku) => {
    try {
      const { data } = await api.post(`/projects/${projectId}/trays/fitting`,
        { point, fitting_sku: sku });
      const next = [...segments, data];
      setSegments(next);
      update && update({ cable_trays: next });
      toast.success(`Inserted ${FITTING_LABELS[data.fitting_type] || "fitting"} at (${point[0]}, ${point[1]})`);
    } catch (e) { toast.error("Insert failed"); console.error(e); }
  };

  const roomReady = !!(project.length_in && project.width_in);
  const selectedSegment = useMemo(() => segments.find((s) => s.id === selectedId) || null, [segments, selectedId]);

  // ── Render
  return (
    <div data-testid="tray-designer">
      {/* Header + auto-route + save */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="label-tag">// Cable Tray Design</div>
          <h3 className="font-display text-lg font-bold">Tray Designer · ABB Cope / Steel City</h3>
          <p className="text-xs text-gray-400">Pick a manufacturer (ABB · Eaton · Legrand · Atkore), drop a stock layout, edit on the 2D plan, drag tray endpoints in 3D, watch live NEC 392 checks.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
                  onClick={runAutoRoute} disabled={!roomReady || busy}
                  data-testid="trays-auto-route">
            <Robot size={14} weight="bold" /> Auto-route
          </button>
          <button className="btn-ghost text-sm py-2 inline-flex items-center gap-2"
                  onClick={addRun} disabled={!roomReady}
                  data-testid="trays-add-run">
            <Plus size={14} /> Add run
          </button>
          <button className="btn-primary text-sm py-2 inline-flex items-center gap-2"
                  onClick={() => saveDesign()} disabled={busy}
                  data-testid="trays-save">
            <FloppyDisk size={14} /> Save
          </button>
        </div>
      </div>

      {/* Catalog defaults */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4 bg-[#0a0d12] border border-white/10 p-4">
        <Field label="Manufacturer">
          <select value={filter.manufacturer_id}
                  onChange={(e) => setFilter({ ...filter, manufacturer_id: e.target.value, series_id: "" })}
                  className="input-base text-sm w-full" data-testid="trays-filter-mfr">
            {(catalog.manufacturers || []).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </Field>
        <Field label="Tray type">
          <select value={filter.tray_type} onChange={(e) => setFilter({ ...filter, tray_type: e.target.value })}
                  className="input-base text-sm w-full" data-testid="trays-filter-type">
            {validTypes.length === 0
              ? <option>—</option>
              : validTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Width (in)">
          <select value={filter.width_in} onChange={(e) => setFilter({ ...filter, width_in: Number(e.target.value) })}
                  className="input-base text-sm w-full" data-testid="trays-filter-width">
            {validWidths.length === 0
              ? <option>—</option>
              : validWidths.map((w) => <option key={w} value={w}>{w}″</option>)}
          </select>
        </Field>
        <Field label="Depth (in)">
          <select value={filter.depth_in} onChange={(e) => setFilter({ ...filter, depth_in: Number(e.target.value) })}
                  className="input-base text-sm w-full" data-testid="trays-filter-depth">
            {validDepths.length === 0
              ? <option>—</option>
              : validDepths.map((d) => <option key={d} value={d}>{d}″</option>)}
          </select>
        </Field>
        <Field label="Material">
          <select value={filter.material} onChange={(e) => setFilter({ ...filter, material: e.target.value })}
                  className="input-base text-sm w-full" data-testid="trays-filter-material">
            {validMaterials.length === 0
              ? <option>—</option>
              : validMaterials.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Default product">
          <select value={defaultSku} onChange={(e) => setDefaultSku(e.target.value)}
                  className="input-base text-sm w-full" data-testid="trays-default-sku">
            <option value="">— choose —</option>
            {products.map((p) => (
              <option key={p.sku} value={p.sku}>
                {p.sku} · {p.material} · ${p.list_price_usd_ft}/ft
              </option>
            ))}
          </select>
        </Field>
        <Field label="Default load class">
          <select value={defaultLC} onChange={(e) => setDefaultLC(e.target.value)}
                  className="input-base text-sm w-full" data-testid="trays-default-loadclass">
            <option>NEMA 8A</option><option>NEMA 12A</option>
            <option>NEMA 16A</option><option>NEMA 20A</option>
          </select>
        </Field>
        <div className="md:col-span-3 flex items-end gap-2">
          <button className="btn-ghost text-sm py-2" onClick={() => applyDefaults(defaultSku, defaultLC, true)}
                  data-testid="trays-apply-defaults"
                  disabled={!defaultSku}>
            Apply to all segments
          </button>
          <button className="btn-ghost text-sm py-2" onClick={() => applyDefaults(defaultSku, defaultLC, false)}
                  disabled={!defaultSku}>
            Apply to missing only
          </button>
        </div>
        <Field label="Largest cable OD (in)">
          <input type="number" step="0.05" value={largestOD}
                 onChange={(e) => setLargestOD(Number(e.target.value) || 0)}
                 onBlur={() => saveDesign()}
                 className="input-base text-sm w-full"
                 data-testid="trays-largest-od" />
        </Field>
        <Field label="Cable weight (lb/ft)">
          <input type="number" step="0.5" value={cableW}
                 onChange={(e) => setCableW(Number(e.target.value) || 0)}
                 onBlur={() => saveDesign()}
                 className="input-base text-sm w-full"
                 data-testid="trays-cable-weight" />
        </Field>
        <Field label="Distributor multiplier">
          <div className="flex gap-1">
            <input type="number" min="0.10" max="1" step="0.01" value={distMult}
                   onChange={(e) => setDistMult(Math.max(0.10, Math.min(1, Number(e.target.value) || 1)))}
                   onBlur={() => saveDesign()}
                   className="input-base text-sm flex-1"
                   data-testid="trays-dist-multiplier" />
            <select value=""
                    onChange={(e) => { if (e.target.value) { setDistMult(Number(e.target.value)); saveDesign(); } }}
                    className="input-base text-xs w-24"
                    data-testid="trays-dist-preset">
              <option value="">Preset</option>
              <option value="0.45">Border 0.45</option>
              <option value="0.55">Graybar 0.55</option>
              <option value="0.58">Wesco 0.58</option>
              <option value="0.65">Rexel 0.65</option>
              <option value="1.00">List × 1.00</option>
            </select>
          </div>
        </Field>
      </div>

      {/* Stock layout templates (D) */}
      <div className="bg-[#0a0d12] border border-white/10 p-3 mb-4" data-testid="trays-templates">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-mono">
            // 1-click layout templates
          </div>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <button key={t.id}
                      onClick={() => applyTemplate(t.id, true)}
                      className="px-3 py-1.5 text-xs font-mono border border-white/10 hover:border-[#00E5FF]/60 text-gray-300"
                      title={t.description}
                      data-testid={`trays-template-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Validator banner */}
      {validation && (
        <ValidationBanner v={validation} />
      )}

      {/* 2D plan editor */}
      <PlanEditor
        project={project} segments={segments}
        selectedId={selectedId} setSelectedId={setSelectedId}
        onUpdate={updateSegment}
        onPlanClick={(pt) => {
          if (insertingFitting) {
            insertFitting(pt, insertingFitting);
            setInsertingFitting(null);
          }
        }}
      />

      {/* Segment list */}
      <div className="mt-4">
        <div className="label-tag mb-2">// Segments</div>
        {segments.length === 0 ? (
          <div className="border border-white/10 bg-[#0a0d12] p-4 text-sm text-gray-500 font-mono">
            No tray segments — click "Auto-route" or "Add run".
          </div>
        ) : (
          <div className="border border-white/10 overflow-x-auto" data-testid="trays-segment-table">
            <table className="w-full text-xs">
              <thead className="bg-[#0a0d12] border-b border-white/10">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500">
                  <th className="p-2">ID</th>
                  <th className="p-2">Kind</th>
                  <th className="p-2">Length ft</th>
                  <th className="p-2">SKU</th>
                  <th className="p-2">Load class</th>
                  <th className="p-2 text-right">Hangers</th>
                  <th className="p-2 text-right">Δ</th>
                </tr>
              </thead>
              <tbody>
                {segments.map((s) => (
                  <SegmentRow key={s.id} segment={s}
                    products={products}
                    selected={s.id === selectedId}
                    onSelect={() => setSelectedId(s.id)}
                    onChange={(patch) => updateSegment(s.id, patch)}
                    onRemove={() => removeSegment(s.id)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      const items = [
                        { label: "Duplicate", icon: <CopySimple size={12} />,
                          hint: "+12 in",
                          onClick: () => duplicateSegment(s.id) },
                      ];
                      // Only `run`/`drop` segments can be joined to another tray
                      // or rotated (fittings inherit rotation from joining trays).
                      if (s.kind !== "fitting") {
                        items.push({
                          label: "Rotate 90° about centerline",
                          icon: <ArrowsClockwise size={12} />,
                          hint: Number(s.rotation_axis_deg) === 90 ? "→ 0°" : "→ 90°",
                          onClick: () => rotateSegment(s.id),
                        });
                        items.push({
                          label: "Suggest fitting to nearest tray",
                          icon: <Lightning size={12} />,
                          onClick: () => setQuickJoinSrc(s),
                        });
                      }
                      items.push({
                        label: "Delete", danger: true, icon: <Trash size={12} />,
                        onClick: () => removeSegment(s.id),
                      });
                      setCtxMenu({ x: e.clientX, y: e.clientY, items });
                    }} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Smart fitting join — pick two trays, get suggested fittings, snap */}
      <JoinTraysPanel
        segments={segments}
        projectId={projectId}
        onJoined={(segs) => {
          setSegments(segs);
          update && update({ cable_trays: segs });
        }}
      />

      {/* Fitting palette */}
      <FittingPalette
        manufacturerId={filter.manufacturer_id}
        seriesId={filter.series_id}
        width={filter.width_in}
        onLoad={loadFittings}
        fittings={fittings}
        activeSku={insertingFitting}
        setActive={setInsertingFitting} />

      {/* BOM rollup — always rendered (with empty state) so the table is
          discoverable and its test-ids exist even before the first run. */}
      <TrayBomTable bom={bom} />

      {/* Right-click menu — rendered last so it stacks above everything else. */}
      <ContextMenu menu={ctxMenu} onClose={() => setCtxMenu(null)} />

      {/* Quick-join dialog — auto-suggests a fitting between the right-clicked
          tray and its nearest neighbour, one-click apply. */}
      <QuickJoinDialog
        open={!!quickJoinSrc}
        sourceSeg={quickJoinSrc}
        projectId={projectId}
        onClose={() => setQuickJoinSrc(null)}
        onJoined={(segs) => {
          setSegments(segs);
          update && update({ cable_trays: segs });
        }}
      />
    </div>
  );
}


/* ─────────── helpers ─────────────────────────────────────────── */
function cryptoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().slice(0, 12);
  return Math.random().toString(36).slice(2, 14);
}

function Field({ label, children }) {
  return (
    <div>
      <div className="label-tag mb-1 text-[10px]">{label}</div>
      {children}
    </div>
  );
}


function ValidationBanner({ v }) {
  const cls = v.errors?.length ? "border-red-500/40 bg-red-500/5 text-red-200"
            : v.warnings?.length ? "border-yellow-500/40 bg-yellow-500/5 text-yellow-200"
            : "border-emerald-500/40 bg-emerald-500/5 text-emerald-200";
  const Icon = v.errors?.length ? XCircle : v.warnings?.length ? WarningCircle : CheckCircle;
  return (
    <div className={`border ${cls} p-3 mb-4 text-xs font-mono`} data-testid="trays-validator">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} weight="fill" />
        <span className="font-bold">
          {v.errors?.length ? `${v.errors.length} error(s)` :
           v.warnings?.length ? `${v.warnings.length} warning(s)` :
           "All NEC 392 checks pass"}
        </span>
        <span className="text-gray-500 ml-auto">
          {v.summary?.run_count || 0} runs · {v.summary?.fitting_count || 0} fittings · {v.summary?.total_length_ft || 0} ft
        </span>
      </div>
      {[...(v.errors||[]), ...(v.warnings||[])].slice(0, 6).map((iss, i) => (
        <div key={`${iss.code}-${i}`} className="pl-5">⚠ <span className="text-[#FFCC00]">{iss.code}</span> · {iss.msg}</div>
      ))}
    </div>
  );
}


function PlanEditor({ project, segments, selectedId, setSelectedId, onUpdate, onPlanClick }) {
  const L = project.length_in || 240, W = project.width_in || 180;
  const pad = 12, w = 760, h = w * (W / L);
  const sx = (x) => pad + (x / L) * (w - 2 * pad);
  const sy = (y) => h - pad - (y / W) * (h - 2 * pad);
  const invX = (px) => Math.round(((px - pad) / (w - 2 * pad)) * L);
  const invY = (py) => Math.round(((h - pad - py) / (h - 2 * pad)) * W);

  // drag = { segId, pointIdx } (endpoint) | { segId, kind: "translate", originPx, originPy, basePts }
  const [drag, setDrag] = useState(null);

  // Clamp an (x, y) endpoint so the tray's cross-section (width_in) stays
  // inside the room walls — half-width margin on every side. Conservative
  // (uses both axes) so orientation-agnostic: an endpoint can never push
  // the tray body through a wall.
  const clampPoint = (seg, x, y) => {
    const margin = Math.max(0, (Number(seg?.width_in) || 12) / 2);
    return [
      Math.max(margin, Math.min(L - margin, x)),
      Math.max(margin, Math.min(W - margin, y)),
    ];
  };

  // Translate an entire segment by (dx, dy) while keeping it inside the
  // room (bounding-box clamp uses the same half-width margin). The tray
  // keeps its shape — no rotation — even when it bumps into a wall.
  const translateSegment = (seg, dx, dy) => {
    const pts = (seg.points || []).map((p) => p.slice());
    if (pts.length === 0) return pts;
    const margin = Math.max(0, (Number(seg?.width_in) || 12) / 2);
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const [px, py] of pts) {
      if (px < minX) minX = px; if (px > maxX) maxX = px;
      if (py < minY) minY = py; if (py > maxY) maxY = py;
    }
    const allowDx = Math.max(margin - (minX + dx), 0)
                  - Math.max((maxX + dx) - (L - margin), 0);
    const allowDy = Math.max(margin - (minY + dy), 0)
                  - Math.max((maxY + dy) - (W - margin), 0);
    const fdx = dx + allowDx;
    const fdy = dy + allowDy;
    return pts.map(([px, py]) => [px + fdx, py + fdy]);
  };

  const onPointerDown = (e, segId, pointIdx) => {
    e.stopPropagation();
    setSelectedId(segId);
    setDrag({ segId, pointIdx });
  };

  const onMidPointerDown = (e, seg) => {
    e.stopPropagation();
    setSelectedId(seg.id);
    const rect = e.currentTarget.ownerSVGElement.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width)  * w;
    const py = ((e.clientY - rect.top)  / rect.height) * h;
    setDrag({
      segId: seg.id,
      kind: "translate",
      originX: invX(px),
      originY: invY(py),
      basePts: (seg.points || []).map((p) => p.slice()),
    });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * w;
    const py = ((e.clientY - rect.top) / rect.height) * h;
    const seg = segments.find((s) => s.id === drag.segId);
    if (!seg) return;

    if (drag.kind === "translate") {
      const dx = invX(px) - drag.originX;
      const dy = invY(py) - drag.originY;
      const moved = translateSegment({ ...seg, points: drag.basePts }, dx, dy)
                      .map(([x, y]) => [Math.round(x), Math.round(y)]);
      onUpdate(drag.segId, { points: moved });
      return;
    }

    const [nx, ny] = clampPoint(seg, invX(px), invY(py));
    const points = seg.points.map(
      (p, i) => i === drag.pointIdx ? [Math.round(nx), Math.round(ny)] : p,
    );
    onUpdate(drag.segId, { points });
  };

  const onPointerUp = () => setDrag(null);

  const onSvgClick = (e) => {
    if (drag) return;   // ignore — drag is finishing
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * w;
    const py = ((e.clientY - rect.top) / rect.height) * h;
    onPlanClick && onPlanClick([invX(px), invY(py)]);
  };

  return (
    <div className="border border-white/10 bg-[#050608] p-3" data-testid="trays-plan-editor">
      <div className="label-tag mb-2">// 2D plan editor · drag green endpoints to reroute · drag cyan ⌖ to move the whole tray · click to drop a fitting</div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full bg-[#0a0d12] border border-white/5 cursor-crosshair"
           onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
           onClick={onSvgClick}>
        {/* Room */}
        <rect x={pad} y={pad} width={w - 2 * pad} height={h - 2 * pad} fill="none" stroke="#00E5FF" strokeWidth="1.5" />
        {/* Equipment */}
        {(project.equipment_list || []).map((it) => {
          const p = it.position || {};
          if (p.x_in === undefined) return null;
          const wi = p.w_in || it.length_in || 24;
          const hi = p.h_in || it.width_in  || 18;
          return (
            <g key={it.id}>
              <rect x={sx(p.x_in)} y={sy(p.y_in + hi)}
                width={sx(p.x_in + wi) - sx(p.x_in)} height={sy(p.y_in) - sy(p.y_in + hi)}
                fill="#3C82B4" fillOpacity="0.35" stroke="#3C82B4" />
              <text x={sx(p.x_in) + 3} y={sy(p.y_in + hi) + 11} fontSize="9" fill="#9fcdef" fontFamily="monospace">{it.tag}</text>
            </g>
          );
        })}
        {/* Tray runs */}
        {segments.filter((s) => s.kind !== "fitting").map((s) => {
          const stroke = s.id === selectedId ? "#FFCC00" : (s.kind === "drop" ? "#7C5CFF" : "#22c55e");
          const segWidth = (s.width_in || 12) / 12;
          const pts = (s.points || []).map(([x, y]) => `${sx(x)},${sy(y)}`).join(" ");
          // Midpoint of the polyline (averaged) — drag handle to translate the
          // whole tray as a block. Stays inside the room (width-aware clamp).
          let mx = 0, my = 0;
          (s.points || []).forEach(([x, y]) => { mx += x; my += y; });
          const n = (s.points || []).length || 1;
          mx /= n; my /= n;
          return (
            <g key={s.id}>
              <polyline points={pts} fill="none" stroke={stroke} strokeWidth={Math.max(2, segWidth * 6)} strokeOpacity="0.5" />
              <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" />
              {(s.points || []).map(([x, y], i) => (
                <circle key={i} cx={sx(x)} cy={sy(y)} r={5}
                  fill={s.id === selectedId ? "#FFCC00" : "#22c55e"}
                  stroke="#0a0d12" strokeWidth="1"
                  className="cursor-grab"
                  data-testid={`trays-handle-${s.id}-${i}`}
                  onPointerDown={(e) => onPointerDown(e, s.id, i)} />
              ))}
              {/* Translate handle — drag to move the whole segment as a block. */}
              {s.kind !== "drop" && (s.points || []).length >= 2 && (
                <g className="cursor-move"
                   onPointerDown={(e) => onMidPointerDown(e, s)}
                   data-testid={`trays-translate-${s.id}`}>
                  <rect x={sx(mx) - 6} y={sy(my) - 6} width="12" height="12"
                        fill="#0a0d12" stroke="#00E5FF" strokeWidth="1.5" />
                  <line x1={sx(mx) - 3} y1={sy(my)} x2={sx(mx) + 3} y2={sy(my)}
                        stroke="#00E5FF" strokeWidth="1.5" />
                  <line x1={sx(mx)} y1={sy(my) - 3} x2={sx(mx)} y2={sy(my) + 3}
                        stroke="#00E5FF" strokeWidth="1.5" />
                </g>
              )}
            </g>
          );
        })}
        {/* Fittings */}
        {segments.filter((s) => s.kind === "fitting").map((f) => {
          const [x, y] = f.points[0];
          return (
            <g key={f.id}
               className="cursor-pointer"
               onClick={(e) => { e.stopPropagation(); setSelectedId(f.id); }}>
              <circle cx={sx(x)} cy={sy(y)} r={7} fill="#FFCC00" stroke="#0a0d12" strokeWidth="2"
                      data-testid={`trays-fitting-${f.id}`} />
              <text x={sx(x) + 10} y={sy(y) - 6} fontSize="9" fill="#FFCC00" fontFamily="monospace">
                {(FITTING_LABELS[f.fitting_type] || "fit")}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="flex gap-4 mt-2 text-[10px] font-mono text-gray-400">
        <span><span className="inline-block w-3 h-3 bg-[#3C82B4]/40 border border-[#3C82B4] mr-1 align-middle" /> Equipment</span>
        <span><span className="inline-block w-3 h-1 bg-[#22c55e] mr-1 align-middle" /> Run</span>
        <span><span className="inline-block w-3 h-1 bg-[#7C5CFF] mr-1 align-middle" /> Drop</span>
        <span><span className="inline-block w-3 h-3 rounded-full bg-[#FFCC00] mr-1 align-middle" /> Fitting</span>
      </div>
    </div>
  );
}


function SegmentRow({ segment, products, selected, onSelect, onChange, onRemove, onContextMenu }) {
  // Local draft for id_display — avoid round-tripping every keystroke through
  // the saveDesign() PUT which would race against the user's typing and snap
  // the input back to the server's response value.
  const [idDraft, setIdDraft] = useState(segment.id_display || "");
  useEffect(() => {
    setIdDraft(segment.id_display || "");
  }, [segment.id, segment.id_display]);
  const commitId = () => {
    const v = idDraft.trim();
    if (v === (segment.id_display || "")) return;
    onChange({ id_display: v });
  };

  const len = useMemo(() => {
    const pts = segment.points || [];
    let inTotal = 0;
    for (let i = 1; i < pts.length; i++) {
      inTotal += Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
    }
    return (inTotal / 12).toFixed(1);
  }, [segment.points]);

  if (segment.kind === "fitting") {
    return (
      <tr className={`border-b border-white/5 ${selected ? "bg-yellow-500/10" : ""}`}
          onClick={onSelect}
          onContextMenu={onContextMenu}
          data-testid={`trays-row-${segment.id}`}>
        <td className="p-2 text-[#FFCC00] font-mono">{segment.id.slice(0, 6)}</td>
        <td className="p-2">{FITTING_LABELS[segment.fitting_type] || "Fitting"}</td>
        <td className="p-2 text-gray-500">—</td>
        <td className="p-2 font-mono">{segment.product_sku}</td>
        <td className="p-2 text-gray-500">—</td>
        <td className="p-2 text-right text-gray-500">—</td>
        <td className="p-2 text-right">
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  className="text-gray-500 hover:text-red-400">
            <Trash size={12} />
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b border-white/5 cursor-pointer ${selected ? "bg-yellow-500/10" : ""}`}
        onClick={onSelect}
        onContextMenu={onContextMenu}
        data-testid={`trays-row-${segment.id}`}>
      <td className="p-2">
        <input
          type="text"
          value={idDraft}
          placeholder={segment.id.slice(0, 6)}
          onChange={(e) => setIdDraft(e.target.value)}
          onBlur={commitId}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.target.blur(); }
            else if (e.key === "Escape") { setIdDraft(segment.id_display || ""); e.target.blur(); }
          }}
          onClick={(e) => e.stopPropagation()}
          className="bg-black/40 border border-white/10 px-2 py-1 font-mono text-xs w-28 text-[#00E5FF] focus:border-[#00E5FF] outline-none"
          title="Display ID (commits on blur/Enter — sync'd with 3D view)"
          data-testid={`trays-row-id-${segment.id}`}
        />
      </td>
      <td className="p-2 capitalize">{segment.kind || "run"}</td>
      <td className="p-2">{len}</td>
      <td className="p-2">
        <select value={segment.product_sku || ""}
                onChange={(e) => onChange({ product_sku: e.target.value || null })}
                onClick={(e) => e.stopPropagation()}
                className="input-base text-xs w-full"
                data-testid={`trays-row-sku-${segment.id}`}>
          <option value="">—</option>
          {products.map((p) => (
            <option key={p.sku} value={p.sku}>{p.sku} · {p.material}</option>
          ))}
        </select>
      </td>
      <td className="p-2">
        <select value={segment.load_class || "NEMA 16A"}
                onChange={(e) => onChange({ load_class: e.target.value })}
                onClick={(e) => e.stopPropagation()}
                className="input-base text-xs w-full">
          <option>NEMA 8A</option><option>NEMA 12A</option>
          <option>NEMA 16A</option><option>NEMA 20A</option>
        </select>
      </td>
      <td className="p-2 text-right">
        <input type="number" min="0" value={segment.hangers ?? ""} placeholder={segment._hangers_needed || "auto"}
               onChange={(e) => onChange({ hangers: e.target.value === "" ? null : Number(e.target.value) })}
               onClick={(e) => e.stopPropagation()}
               className="input-base text-xs w-16 text-right" />
      </td>
      <td className="p-2 text-right">
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }}
                className="text-gray-500 hover:text-red-400" data-testid={`trays-row-remove-${segment.id}`}>
          <Trash size={12} />
        </button>
      </td>
    </tr>
  );
}


function FittingPalette({ manufacturerId, seriesId, width, onLoad, fittings, activeSku, setActive }) {
  useEffect(() => { onLoad(seriesId, width); }, [manufacturerId, seriesId, width]);   // eslint-disable-line

  return (
    <div className="mt-4 border border-white/10 bg-[#0a0d12] p-3" data-testid="trays-fitting-palette">
      <div className="label-tag mb-2">// Fittings · click one, then click on the plan to drop it</div>
      {fittings.length === 0 ? (
        <div className="text-xs text-gray-500 font-mono">No fittings match — adjust width/material in the catalog filter above.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {fittings.map((f) => (
            <button key={f.sku}
                    onClick={() => setActive(activeSku === f.sku ? null : f.sku)}
                    className={`px-2 py-1 text-xs font-mono border ${activeSku === f.sku
                      ? "border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00]"
                      : "border-white/10 text-gray-300 hover:border-white/30"}`}
                    data-testid={`trays-fitting-btn-${f.sku}`}>
              {FITTING_LABELS[f.fitting_type] || f.fitting_type} · {f.width_in}″
              <span className="text-gray-500 ml-1">${f.list_price_usd}</span>
            </button>
          ))}
        </div>
      )}
      {activeSku && (
        <div className="mt-2 text-[10px] text-[#FFCC00] font-mono">
          ⤴ Click anywhere on the plan to place <b>{activeSku}</b> — or click the button again to cancel.
        </div>
      )}
    </div>
  );
}


function TrayBomTable({ bom }) {
  const lines = bom?.lines || [];
  const grandTotal = bom?.totals?.grand_total_usd || 0;
  const lineCount  = bom?.totals?.line_count || 0;
  const netTotal   = bom?.totals?.net_total_usd;
  const mult       = bom?.totals?.distributor_multiplier;
  const showNet = mult != null && mult < 1;
  return (
    <div className="mt-4 border border-white/10" data-testid="trays-bom">
      <div className="bg-[#0a0d12] p-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-bold text-sm inline-flex items-center gap-2">
          <Cube size={16} className="text-[#00E5FF]" /> Tray BOM rollup
        </div>
        <div className="text-sm font-mono" data-testid="trays-bom-total">
          <span className="text-gray-400">List </span>
          <span className="text-[#FFCC00]">${grandTotal.toLocaleString()}</span>
          {showNet && (
            <>
              <span className="mx-2 text-gray-500">·</span>
              <span className="text-gray-400">Net (×{mult.toFixed(2)}) </span>
              <span className="text-emerald-400" data-testid="trays-bom-net-total">
                ${netTotal.toLocaleString()}
              </span>
            </>
          )}
          <span className="text-gray-500 ml-2 text-[10px]">{lineCount} lines</span>
        </div>
      </div>
      {lines.length === 0 ? (
        <div className="p-4 text-xs text-gray-500 font-mono" data-testid="trays-bom-empty">
          No tray items yet — add a run and pick a catalog SKU. The rollup updates automatically.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-[#0a0d12] border-b border-white/10">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-gray-500">
              <th className="p-2">SKU</th>
              <th className="p-2">Label</th>
              <th className="p-2">Category</th>
              <th className="p-2 text-right">Qty</th>
              <th className="p-2">Unit</th>
              <th className="p-2 text-right">Unit $</th>
              {showNet && <th className="p-2 text-right">Net unit $</th>}
              <th className="p-2 text-right">Ext $</th>
              {showNet && <th className="p-2 text-right">Net ext $</th>}
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <tr key={line.sku} className="border-b border-white/5 font-mono">
                <td className="p-2 text-[#00E5FF]">{line.sku}</td>
                <td className="p-2 text-gray-300">{line.label}</td>
                <td className="p-2 capitalize text-gray-500">{line.category.replace(/_/g, " ")}</td>
                <td className="p-2 text-right">{line.quantity}</td>
                <td className="p-2 text-gray-500">{line.unit}</td>
                <td className="p-2 text-right">${(line.unit_price_usd ?? 0).toFixed(2)}</td>
                {showNet && <td className="p-2 text-right text-emerald-400">${(line.net_unit_price_usd ?? 0).toFixed(2)}</td>}
                <td className="p-2 text-right text-[#FFCC00]">${line.ext_price_usd.toFixed(2)}</td>
                {showNet && <td className="p-2 text-right text-emerald-400">${(line.net_ext_price_usd ?? 0).toFixed(2)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {bom?.reference && (
        <div className="bg-[#0a0d12] p-2 text-[10px] text-gray-500 font-mono border-t border-white/10">
          {bom.reference}
        </div>
      )}
    </div>
  );
}
